import { useErrorHandler } from '@app/core/hooks/useErrorHandler';
import { queryClient } from '@app/core/integrations/react-query/react-query';
import { useToast } from '@app/core/notifications';
import { Station } from '@app/core/types/stations';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import {
  useVideoEditorUpdateCompositionsById,
  videoEditorGetCompositionsByIdQueryKey,
} from '@mi-front/api-v2';
import { useRouter } from '@mi-front/core';
import { defineMessage, useIntl } from 'react-intl';

const composeEditSuccessToastMessage = defineMessage({
  id: 'compose.editPage.successToast',
  defaultMessage: 'Composición actualizada',
});

interface UseComposeEditCompositionParams {
  compositionId: string;
  station: Station | undefined;
}

/**
 * Updates a composition name, refreshes detail and list caches, then navigates back to the detail view.
 */
export function useComposeEditComposition({
  compositionId,
  station,
}: UseComposeEditCompositionParams) {
  const { history } = useRouter();
  const { $t } = useIntl();
  const { notifySuccess } = useToast();
  const { onApiError } = useErrorHandler();
  const listId = station ? `compose-compositions-list-${station.slug}` : undefined;

  const mutation = useVideoEditorUpdateCompositionsById();

  async function submitEdit(name: string) {
    try {
      await mutation.mutateAsync({
        id: compositionId,
        data: { name: name.trim() },
      });

      await queryClient.invalidateQueries({
        queryKey: videoEditorGetCompositionsByIdQueryKey(compositionId),
      });

      if (listId) {
        await queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === listId,
        });
      }

      notifySuccess($t(composeEditSuccessToastMessage));
      history.push(composeRouteGenerator.edits(compositionId));
    } catch (error) {
      onApiError(error);
    }
  }

  return {
    submitEdit,
    isSubmitting: mutation.isPending,
  };
}
